import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, message } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined, SolutionOutlined, ShopOutlined } from '@ant-design/icons';
import { useLocale } from '../context/LocaleContext';
import { authApi } from '../api';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLocale();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values: any) => {
    if (loading) return;
    if (values.password !== values.confirmPassword) {
      message.error(t('两次密码不一致'));
      return;
    }
    setLoading(true);
    try {
      const res: any = await authApi.register({
        real_name: values.real_name,
        store_name: values.store_name,
        password: values.password,
        email: values.email,
        phone: values.phone,
        address: values.address,
        salesperson_account: values.salesperson_account,
      });
      if (res?.code === 0) {
        navigate('/register/success', {
          state: {
            store_no: res.data?.store_no,
            username: res.data?.username,
            salesperson_name: res.data?.salesperson_name,
            store_name: values.store_name,
          },
          replace: true,
        });
      } else {
        if (res?.code === 404002 || res?.code === 400005) {
          message.error(t('设计师账号不存在，请输入正确的设计师名称'));
        } else {
          message.error(res?.message || t('注册失败'));
        }
      }
    } catch (e: any) {
      // HTTP 4xx/5xx 时 axios 直接抛异常，不会走上面的 res.code 分支。
      // 后端真实提示在拦截器挂上的 responseData.message 里（如「店铺名已注册，您的门店编号为 Cxxxx，请直接登录」），
      // 若只取 e.message 会显示 axios 通用文案 "Request failed with status code 400"，
      // 把可预期的业务校验拒绝误显示成系统故障。
      const errCode = e?.code ?? e?.responseData?.code;
      const backendMsg = e?.responseData?.message;
      if (errCode === 404002 || errCode === 400005) {
        message.error(t('设计师账号不存在，请输入正确的设计师名称'));
      } else {
        message.error(backendMsg || e?.message || t('注册失败'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f4f8' }}>
      {/* 右上角联系我们 */}
      <div style={{ position: 'fixed', top: 20, right: 24, zIndex: 100 }}>
        <a href="mailto:info@jdsuit.com" style={{ fontSize: 13, color: '#1a5fb4', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
          <MailOutlined style={{ fontSize: 14 }} />
          {t('联系我们')}: info@jdsuit.com
        </a>
      </div>
      <div style={{ width: '100%', maxWidth: 420, margin: '0 16px', padding: 40, background: '#fff', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#1a5fb4' }}>{t('注册账号')}</div>
          <div style={{ fontSize: 14, color: '#666', marginTop: 8 }}>{t('填写信息完成门店注册')}</div>
        </div>
        <Form layout="vertical" onFinish={onFinish}>
          <Form.Item name="real_name" label={t('用户姓名')}
            rules={[{ required: true, message: t('请输入用户姓名') }]}>
            <Input prefix={<UserOutlined />} placeholder={t('请输入您的姓名')} />
          </Form.Item>
          <Form.Item name="store_name" label={t('店铺名')}
            rules={[{ required: true, message: t('请输入店铺名') }]}>
            <Input prefix={<ShopOutlined />} placeholder={t('请输入店铺名称')} />
          </Form.Item>
          <Form.Item name="password" label={t('密码')}
            rules={[{ required: true, message: t('请输入密码') }, { min: 6, message: t('密码至少6位') }]}>
            <Input.Password prefix={<LockOutlined />} placeholder={t('请输入密码')} />
          </Form.Item>
          <Form.Item name="confirmPassword" label={t('确认密码')}
            rules={[{ required: true, message: t('请确认密码') }]}>
            <Input.Password prefix={<LockOutlined />} placeholder={t('请再次输入密码')} />
          </Form.Item>
          <Form.Item name="email" label={t('邮箱')}>
            <Input prefix={<MailOutlined />} placeholder={t('请输入邮箱')} />
          </Form.Item>
          <Form.Item name="phone" label={t('电话')}>
            <Input prefix={<PhoneOutlined />} placeholder={t('请输入电话')} />
          </Form.Item>
          <Form.Item name="address" label={t('客户地址')}>
            <Input prefix={<EnvironmentOutlined />} placeholder={t('请输入客户地址')} />
          </Form.Item>
          <Form.Item
            name="salesperson_account"
            label={t('所属业务员账号')}
            extra={t('选填：知道负责您的业务员账号可填写，将由我们为您对接；留空则由客服为您分配')}
          >
            <Input prefix={<SolutionOutlined />} placeholder={t('选填，请输入负责您的业务员账号')} />
          </Form.Item>
          <Form.Item style={{ marginTop: 24 }}>
            <Button type="primary" htmlType="submit" loading={loading} block>
              {t('注册')}
            </Button>
          </Form.Item>
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <span style={{ color: '#666' }}>{t('已有账号？')}</span>
            <Button type="link" onClick={() => navigate('/login')}>
              {t('立即登录')}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default Register;
